// src/hooks/useAppointmentMove.ts
import { useCallback, useState } from "react";
import axios from "axios";

import { updateAppointment } from "../../api/healthcare/Appointment/AppointmentApi";
import { useNotifications } from "../../context/NotificationContext";
import { Appointment, AppointmentSaveRequest } from "../../types/appointments/AppointmentTypes";
import { RescheduleMove, canRescheduleAppointment, isSameSlot, rescheduleTarget } from "../../utils/appointments/reschedule";
import type { UserUuid } from "../../types/identity";

/** One entry of healthcare's `unavailable_block` 409 payload. */
export interface UnavailableBlockNotice {
	starts_at: string;
	ends_at: string;
	reason: string | null;
}

export interface PendingReschedule {
	appointment: Appointment;
	start: Date;
	end: Date;
	practitionerId: UserUuid;
	action: "move" | "resize";
	// Set once healthcare has refused with `unavailable_block`; the same move then retries acknowledged.
	blocks: UnavailableBlockNotice[] | null;
}

/** Where the calendar should paint the appointment while its write is in flight. */
export interface OptimisticMove {
	id: number;
	start: Date;
	end: Date;
	practitionerId: UserUuid;
}

export const MOVE_FAILED = "Appointment not moved";
export const MOVE_FAILED_UNKNOWN = "The appointment could not be moved.";

interface AppointmentEventLike {
	resource: { appointment: Appointment };
}

const unavailableBlocksFrom = (error: unknown): UnavailableBlockNotice[] | null => {
	if (!axios.isAxiosError(error) || error.response?.status !== 409) return null;

	const data: any = error.response.data;

	return data?.code === "unavailable_block" ? (data.blocks ?? []) : null;
};

// ⚠️ Verbatim: a refusal is a POLICY answer (notice window, end-before-start, double booking) and
// the practitioner needs healthcare's own words to know why, not a generic failure.
const refusalMessage = (error: unknown): string => {
	if (axios.isAxiosError(error)) {
		const data: any = error.response?.data;
		if (typeof data?.error === "string") return data.error;
		if (typeof data?.message === "string") return data.message;
		if (data?.errors) return Object.values(data.errors).flat().join(" ");
	}

	return MOVE_FAILED_UNKNOWN;
};

const isDoubleBooking = (error: unknown): boolean =>
	axios.isAxiosError(error) && error.response?.status === 409 && error.response?.data?.code !== "unavailable_block";

interface UseAppointmentMoveOptions {
	onMoved?: () => void;
	practitionerName?: (id: UserUuid) => string;
}

export const useAppointmentMove = ({ onMoved, practitionerName }: UseAppointmentMoveOptions = {}) => {
	const { showError } = useNotifications();
	const [pending, setPending] = useState<PendingReschedule | null>(null);
	const [optimistic, setOptimistic] = useState<OptimisticMove | null>(null);
	const [movingId, setMovingId] = useState<number | null>(null);

	// ⚠️ Every drop is confirmed. A drag is an easy misclick and the write reaches the patient's reminders.
	const requestMove = useCallback((event: AppointmentEventLike, move: RescheduleMove) => {
		const appointment = event.resource.appointment;
		if (!canRescheduleAppointment(appointment)) return;

		const target = rescheduleTarget(appointment, move);
		if (isSameSlot(appointment, target)) return;

		setPending({ appointment, ...target, action: move.action, blocks: null });
	}, []);

	const commit = useCallback(
		async (next: PendingReschedule, acknowledge: boolean) => {
			const { appointment } = next;

			setMovingId(appointment.id);
			setOptimistic({ id: appointment.id, start: next.start, end: next.end, practitionerId: next.practitionerId });

			// The drawer's own save with the confirmation prompt declined — a drag never emails the patient.
			const payload: AppointmentSaveRequest = {
				...appointment,
				start_time: next.start,
				end_time: next.end,
				practitioner_id: next.practitionerId,
				send_confirmation: false,
			};

			try {
				await updateAppointment(payload, acknowledge);
				setPending(null);
				onMoved?.();
			} catch (error) {
				setOptimistic(null);

				const blocks = unavailableBlocksFrom(error);
				if (blocks && !acknowledge) {
					setPending({ ...next, blocks });
					return;
				}

				setPending(null);
				const reason = refusalMessage(error);
				const summary = isDoubleBooking(error) ? `${practitionerName?.(next.practitionerId) ?? "That practitioner"} already has an appointment then` : MOVE_FAILED;
				showError(summary, reason);
			} finally {
				setMovingId(null);
			}
		},
		[onMoved, practitionerName, showError]
	);

	const confirmPending = useCallback(() => {
		if (!pending) return;
		commit(pending, pending.blocks !== null);
	}, [pending, commit]);

	const cancelPending = useCallback(() => {
		setPending(null);
		setOptimistic(null);
	}, []);

	return { requestMove, pending, confirmPending, cancelPending, optimistic, movingId };
};
